import React from 'react'
import { intl } from 'lang/intl'

import { TtButton, TtIcon, TtTrayChyronComingUp } from '@23/ui-react'

import {
  removeOverlay,
  setChyron,
  useOverlayState,
} from '../../../../firebase/hooks/useOverlays'
import { type TrayCardComingUp } from '../../../../firebase/model/tray-cards/types/coming-up-model'
import { type PropsWithTrayItemChildren } from '../TrayItem'
import { setTrayCardEditing, useTrayCardEditingId } from '../useTrayEditing'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import {
  setActiveCard,
  updateComingUpCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../firebase/hooks/useTray'

interface ComingUpCardProps {
  id: string
}

const ComingUpCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<ComingUpCardProps>) => {
  const card = useTrayCard(id) as TrayCardComingUp | undefined
  const cardState = useTrayCardState(id)
  const cardBeingEdited = useTrayCardEditingId()
  const overlay = useOverlayState()

  const isCurrentlyEditing = cardBeingEdited === id

  const isChyronShowing = overlay?.type === ChyronType.ComingUp

  if (card?.type !== 'coming-up') return null

  const showChyron = () => {
    if (isChyronShowing && overlay.cardId === card.id) return
    if (!card.text) return
    setChyron({
      type: ChyronType.ComingUp,
      text: card.text,
      cardId: card.id,
    })
  }

  const onCardClickHandler = () => {
    showChyron()
    setActiveCard(id)
  }

  const onValueChangeHandler = (text: string) => {
    updateComingUpCard(id, text)
    setTrayCardEditing(null)
  }

  return (
    <TtTrayChyronComingUp
      tag={intl.formatMessage({ defaultMessage: 'Coming up' })}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      editing={isCurrentlyEditing}
      placeholder={intl.formatMessage({
        defaultMessage: "What's coming up next?",
      })}
      value={card.text}
      onTtClick={(event) => {
        if (event.target === event.currentTarget) {
          onCardClickHandler()
        }
      }}
      onTtChange={(e) => onValueChangeHandler(e.detail.value)}
    >
      <TtButton
        slot="toolbar"
        disabled={!card.text}
        label={
          isChyronShowing
            ? intl.formatMessage({
                defaultMessage: 'Remove the coming-up TvCard',
              })
            : card.text.length === 0
              ? intl.formatMessage({
                  defaultMessage:
                    'Start typing to activate the coming-up TvCard',
                })
              : intl.formatMessage({
                  defaultMessage: 'Display coming-up TvCard',
                })
        }
        color="secondary"
        variant="ghost"
        size="x-small"
        circle
        onClick={isChyronShowing ? removeOverlay : showChyron}
      >
        <TtIcon
          library="one/webinars"
          name={isChyronShowing ? 'visibility-off' : 'visibility-on'}
        />
      </TtButton>
      {children?.({ canEdit: true })}
    </TtTrayChyronComingUp>
  )
}

export default ComingUpCard
